import { create } from 'zustand';
import type { Project, ProjectMember, ProjectRole, ProjectMemberSummary } from '../types';
import { projectApi, projectMemberApi } from '../services/api';
import { extractErrorResult } from '../utils/errorMessages';
import { showToast } from '../components/common';
import { useOrganizationStore } from './organizationStore';
import { websocketService } from '../services/websocket';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  projectMembers: ProjectMember[];
  memberSummary: ProjectMemberSummary | null;
  isLoading: boolean;
  error: string | null;
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  createProject: (data: { name: string; description?: string; color?: string }) => Promise<Project | null>;
  updateProject: (id: string, data: Partial<{ name: string; description: string; color: string }>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  archiveProject: (id: string) => Promise<void>;
  // Feature 6: Project membership methods
  fetchProjectMembers: (projectId: string) => Promise<void>;
  addProjectMember: (projectId: string, userId: string, role?: ProjectRole) => Promise<ProjectMember | null>;
  bulkAddProjectMembers: (projectId: string, members: Array<{ userId: string; role?: ProjectRole }>) => Promise<{ added: ProjectMember[]; errors: Array<{ userId: string; error: string }> } | null>;
  updateMemberRole: (projectId: string, userId: string, role: ProjectRole) => Promise<boolean>;
  removeProjectMember: (projectId: string, userId: string) => Promise<boolean>;
  fetchMemberSummary: (projectId: string) => Promise<void>;
  clearCurrentProject: () => void;
  // Real-time WebSocket subscriptions
  subscribeToRealTimeUpdates: () => void;
  unsubscribeFromRealTimeUpdates: () => void;
}

// Real-time WebSocket subscription cleanup functions
const realtimeUnsubscribers: Array<() => void> = [];

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  projectMembers: [],
  memberSummary: null,
  isLoading: false,
  error: null,

  fetchProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const activeOrgId = useOrganizationStore.getState().activeOrganizationId;
      const response = await projectApi.list({ includeArchived: 'true', organizationId: activeOrgId });
      if (response.success && response.data) {
        set({ projects: response.data as Project[], isLoading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch projects. Please try again.', isLoading: false });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
    }
  },

  fetchProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectApi.get(id);
      if (response.success && response.data) {
        set({ currentProject: response.data as Project, isLoading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch project. Please try again.', isLoading: false });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
    }
  },

  createProject: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectApi.create(data as unknown as Record<string, unknown>);
      if (response.success && response.data) {
        const newProject = response.data as Project;
        const { projects } = get();
        // Dedup: skip if already added via WebSocket event
        if (!projects.some((p) => p.id === newProject.id)) {
          set({ projects: [newProject, ...projects], isLoading: false });
        } else {
          set({ isLoading: false });
        }
        return newProject;
      } else {
        set({ error: response.error?.message || 'Failed to create project. Please check your input and try again.', isLoading: false });
        return null;
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateProject: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectApi.update(id, data as unknown as Record<string, unknown>);
      if (response.success && response.data) {
        const updatedProject = response.data as Project;
        set({
          projects: get().projects.map((p) => (p.id === id ? updatedProject : p)),
          currentProject: get().currentProject?.id === id ? updatedProject : get().currentProject,
          isLoading: false,
        });
      } else {
        set({ error: response.error?.message || 'Failed to update project. Please try again.', isLoading: false });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
    }
  },

  deleteProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectApi.delete(id);
      if (response.success) {
        set({
          projects: get().projects.filter((p) => p.id !== id),
          currentProject: get().currentProject?.id === id ? null : get().currentProject,
          isLoading: false,
        });
        showToast('Project deleted', 'success');
      } else {
        set({ error: response.error?.message || 'Failed to delete project. Please try again.', isLoading: false });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
    }
  },

  archiveProject: async (id) => {
    try {
      const response = await projectApi.archive(id);
      if (response.success && response.data) {
        const updatedProject = response.data as Project;
        set({
          projects: get().projects.map((p) => (p.id === id ? updatedProject : p)),
        });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
    }
  },

  // Feature 6: Project membership methods
  fetchProjectMembers: async (projectId) => {
    try {
      const response = await projectMemberApi.list(projectId);
      if (response.success && response.data) {
        set({ projectMembers: response.data as ProjectMember[] });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
    }
  },

  addProjectMember: async (projectId, userId, role = 'MEMBER') => {
    try {
      const response = await projectMemberApi.add(projectId, { userId, role });
      if (response.success && response.data) {
        const newMember = response.data as ProjectMember;
        set({ projectMembers: [...get().projectMembers, newMember] });
        return newMember;
      }
      return null;
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
      return null;
    }
  },

  bulkAddProjectMembers: async (projectId, members) => {
    try {
      const response = await projectMemberApi.bulkAdd(projectId, members);
      if (response.success && response.data) {
        const result = response.data as { added: ProjectMember[]; errors: Array<{ userId: string; error: string }> };
        // Refresh the member list
        await get().fetchProjectMembers(projectId);
        return result;
      }
      return null;
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
      return null;
    }
  },

  updateMemberRole: async (projectId, userId, role) => {
    try {
      const response = await projectMemberApi.updateRole(projectId, userId, role);
      if (response.success && response.data) {
        const updatedMember = response.data as ProjectMember;
        set({
          projectMembers: get().projectMembers.map((m) =>
            m.userId === userId ? updatedMember : m
          ),
        });
        return true;
      } else {
        return false;
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
      return false;
    }
  },

  removeProjectMember: async (projectId, userId) => {
    try {
      const response = await projectMemberApi.remove(projectId, userId);
      if (response.success) {
        set({
          projectMembers: get().projectMembers.filter((m) => m.userId !== userId),
        });
        return true;
      } else {
        return false;
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
      return false;
    }
  },

  fetchMemberSummary: async (projectId) => {
    try {
      const response = await projectMemberApi.getSummary(projectId);
      if (response.success && response.data) {
        set({ memberSummary: response.data as ProjectMemberSummary });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
    }
  },

  clearCurrentProject: () => {
    set({ currentProject: null, projectMembers: [], memberSummary: null });
  },

  subscribeToRealTimeUpdates: () => {
    // Unsubscribe from any existing subscriptions first
    get().unsubscribeFromRealTimeUpdates();

    // project:created — prepend new project (dedup: skip if already exists)
    realtimeUnsubscribers.push(
      websocketService.onProjectCreated((data) => {
        const { projects } = get();
        if (!projects.some((p) => p.id === data.project.id)) {
          set({ projects: [data.project, ...projects] });
        }
      })
    );

    // project:updated — replace matching project in array and currentProject
    realtimeUnsubscribers.push(
      websocketService.onProjectUpdated((data) => {
        set({
          projects: get().projects.map((p) => (p.id === data.project.id ? data.project : p)),
          currentProject: get().currentProject?.id === data.project.id ? data.project : get().currentProject,
        });
      })
    );

    // project:deleted — remove from array and clear currentProject if it matches
    realtimeUnsubscribers.push(
      websocketService.onProjectDeleted((data) => {
        set({
          projects: get().projects.filter((p) => p.id !== data.projectId),
          currentProject: get().currentProject?.id === data.projectId ? null : get().currentProject,
        });
      })
    );

    // project:archived — replace matching project (toggle isArchived)
    realtimeUnsubscribers.push(
      websocketService.onProjectArchived((data) => {
        set({
          projects: get().projects.map((p) => (p.id === data.project.id ? data.project : p)),
          currentProject: get().currentProject?.id === data.project.id ? data.project : get().currentProject,
        });
      })
    );
  },

  unsubscribeFromRealTimeUpdates: () => {
    realtimeUnsubscribers.forEach((unsub) => unsub());
    realtimeUnsubscribers.length = 0;
  },
}));
